import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { useProfile } from '@/lib/hooks/use-profile'
import { useOwnFreelancerProfile } from '@/lib/hooks/use-own-freelancer-profile'
import { useOwnClientProfile } from '@/lib/hooks/use-own-client-profile'
import { BrainLogo } from '@/components/ui/brain-logo'
import { AnimatedLoader } from '@/components/ui/animated-loader'
import { toast } from 'sonner'
import { Building2, Sparkles, ArrowRight } from 'lucide-react'
import { SlideIn, FadeIn, StaggerContainer, StaggerItem, ScaleIn } from '@/components/motion'
import type { Database } from '@/lib/supabase/supabase-types'
import { emailService } from '@/lib/api/services/email.service'

type OnboardingStep = 'role' | 'complete'

export interface OnboardingFormData {
  is_genius: boolean
  is_client: boolean
  // Genius/Freelancer fields (for freelancer_profiles table)
  display_name: string
  headline: string
  freelancer_bio: string
  skills: string[]
  linkedin_url: string
  website_url: string
  location: string
  hourly_rate: string
  is_searchable: boolean
  freelancer_email: string
  // Client fields (for client_profiles table)
  company_name: string
  company_size: string
  industry: string
  client_website_url: string
  client_bio: string
  client_location: string
  client_email: string
}

export default function Onboarding() {
  const navigate = useNavigate()
  const { user, isLoaded: isUserLoaded } = useUser()
  const { profile, updateProfile } = useProfile()
  const { saveFreelancerProfile } = useOwnFreelancerProfile()
  const { saveClientProfile } = useOwnClientProfile()
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('role')
  const [selectedRole, setSelectedRole] = useState<'genius' | 'client' | null>(null)

  // Extract user data from Clerk
  const getUserEmail = () => user?.primaryEmailAddress?.emailAddress || ''
  const getUserDisplayName = () => {
    if (!user) return ''
    return user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ') || ''
  }

  const [formData, setFormData] = useState<OnboardingFormData>({
    is_genius: false,
    is_client: false,
    // Genius/Freelancer fields
    display_name: '',
    headline: '',
    freelancer_bio: '',
    skills: [],
    linkedin_url: '',
    website_url: '',
    location: '',
    hourly_rate: '',
    is_searchable: true,
    freelancer_email: '',
    // Client fields
    company_name: '',
    company_size: '',
    industry: '',
    client_website_url: '',
    client_bio: '',
    client_location: '',
    client_email: '',
  })

  // Pre-populate form data with Clerk user information when user data loads
  useEffect(() => {
    if (isUserLoaded && user) {
      const userEmail = getUserEmail()
      const userDisplayName = getUserDisplayName()

      setFormData(prev => ({
        ...prev,
        display_name: userDisplayName,
        freelancer_email: userEmail,
        client_email: userEmail,
      }))
    }
  }, [isUserLoaded, user])

  if (!profile || !isUserLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <AnimatedLoader message="Loading..." />
      </div>
    )
  }

  const selectRole = async (role: 'genius' | 'client') => {
    setSelectedRole(role)
    // Immediately complete onboarding with minimal data - no form steps
    const updatedFormData = {
      ...formData,
      is_genius: role === 'genius',
      is_client: role === 'client',
    }
    setFormData(updatedFormData)
    await handleQuickComplete(role, updatedFormData)
  }

  const handleQuickComplete = async (role: 'genius' | 'client', data: OnboardingFormData) => {
    setCurrentStep('complete')

    try {
      // Prepare user profile update data
      const userUpdates: Database['public']['Tables']['users']['Update'] = {
        is_genius: role === 'genius',
        is_client: role === 'client',
        onboarding_completed: true,
      }

      // Create freelancer profile with minimal data from Clerk
      if (role === 'genius' && saveFreelancerProfile) {
        saveFreelancerProfile({
          display_name: data.display_name || getUserDisplayName() || 'AI Expert',
          headline: null,
          bio: null,
          skills: null,
          linkedin_url: null,
          website_url: null,
          location: null,
          hourly_rate_cents: null,
          is_searchable: true,
          profile_picture_url: null,
          email: data.freelancer_email || getUserEmail() || null,
        })
      }

      // Create client profile with minimal data from Clerk
      if (role === 'client' && saveClientProfile) {
        saveClientProfile({
          company_name: 'Untitled Company',
          company_size: null,
          industry: null,
          location: null,
          bio: null,
          website_url: null,
          profile_picture_url: null,
          email: data.client_email || getUserEmail() || null,
        })
      }

      // Update user profile - this will trigger role context updates
      if (updateProfile) {
        updateProfile(userUpdates)
      }

      // Send onboarding email with role-specific next steps (non-blocking)
      const userEmail = getUserEmail()
      if (userEmail) {
        emailService.sendOnboardingRoleSelectedEmail(userEmail, role).catch((error) => {
          console.error('Failed to send onboarding email:', error)
          // Don't show error to user, email sending is non-critical
        })
      }

      // Show success message
      toast.success('Welcome to AI Expert Network!', {
        description: 'Your account has been created. Complete your profile to get started.',
      })

      // Navigate to the appropriate dashboard after a short delay
      setTimeout(() => {
        navigate(role === 'client' ? '/client/dashboard' : '/genius/dashboard', { replace: true })
      }, 1500)
    } catch (error) {
      console.error('Error completing onboarding:', error)
      toast.error('Failed to complete setup. Please try again.')
      setCurrentStep('role')
      setSelectedRole(null)
    }
  }


  // Role Selection Step
  if (currentStep === 'role') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <ScaleIn className="w-full max-w-[900px]">
          <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
            {/* Header with gradient */}
            <div
              className="pt-16 px-8 pb-12 flex flex-col items-center"
              style={{
                background: 'linear-gradient(180deg, rgba(248, 249, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)',
              }}
            >
              <FadeIn delay={0.1}>
                <BrainLogo size="lg" variant="light" showGlow={false} />
              </FadeIn>
              <SlideIn direction="up" delay={0.2}>
                <h1 className="text-4xl md:text-5xl font-black text-slate-900 text-center mt-6">
                  Welcome! Let's Get Started
                </h1>
              </SlideIn>
              <SlideIn direction="up" delay={0.3}>
                <p className="text-xl text-slate-500 text-center mt-4">
                  Choose your path to begin your journey
                </p>
              </SlideIn>
            </div>

            {/* Role Cards */}
            <StaggerContainer staggerDelay={0.15} delayChildren={0.4} className="px-8 md:px-16 pb-16 flex flex-col md:flex-row gap-8 justify-center">
              {/* Client Card */}
              <StaggerItem className="flex-1 max-w-[380px]">
                <button
                  onClick={() => selectRole('client')}
                  className="w-full bg-[#e9f1fe] rounded-[40px] p-8 pt-12 flex flex-col items-center text-center hover:shadow-lg transition-all hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-[#4669ed]/20"
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                    style={{
                      background: 'linear-gradient(145.3deg, rgba(54, 84, 233, 1) 0%, rgba(70, 105, 237, 1) 100%)',
                    }}
                  >
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-[32px] font-extrabold text-slate-900 mb-4">
                    Hire AI Experts
                  </h3>
                  <p className="text-lg text-slate-500 leading-relaxed mb-8">
                    Find and hire pre-vetted AI
                    <br />
                    specialists for your project
                  </p>
                  <div
                    className="rounded-[48px] py-4 px-8 flex items-center gap-2"
                    style={{
                      background: 'linear-gradient(108.7deg, rgba(54, 84, 233, 1) 0%, rgba(70, 105, 237, 1) 100%)',
                    }}
                  >
                    <span className="text-white font-bold">Continue as Client</span>
                    <ArrowRight className="w-6 h-6 text-white" />
                  </div>
                </button>
              </StaggerItem>

              {/* Expert Card */}
              <StaggerItem className="flex-1 max-w-[380px]">
                <button
                  onClick={() => selectRole('genius')}
                  className="w-full bg-[#e9f1fe] rounded-[40px] p-8 pt-12 flex flex-col items-center text-center hover:shadow-lg transition-all hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-[#4669ed]/20"
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                    style={{
                      background: 'linear-gradient(145.3deg, rgba(54, 84, 233, 1) 0%, rgba(70, 105, 237, 1) 100%)',
                    }}
                  >
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-[32px] font-extrabold text-slate-900 mb-4">
                    Join as Expert
                  </h3>
                  <p className="text-lg text-slate-500 leading-relaxed mb-8">
                    Showcase your AI expertise and
                    <br />
                    get matched with top companies
                  </p>
                  <div
                    className="rounded-[48px] py-4 px-8 flex items-center gap-2"
                    style={{
                      background: 'linear-gradient(108.31deg, rgba(54, 84, 233, 1) 0%, rgba(70, 105, 237, 1) 100%)',
                    }}
                  >
                    <span className="text-white font-bold">Continue as Expert</span>
                    <ArrowRight className="w-6 h-6 text-white" />
                  </div>
                </button>
              </StaggerItem>
            </StaggerContainer>
          </div>
        </ScaleIn>
      </div>
    )
  }

  // Complete Step - shows while setting up account
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <ScaleIn className="text-center">
        <AnimatedLoader message="Setting up your account..." />
        <FadeIn delay={0.3}>
          <p className="text-slate-400 mt-4">
            Redirecting to your {selectedRole === 'client' ? 'client' : 'expert'} dashboard...
          </p>
        </FadeIn>
      </ScaleIn>
    </div>
  )
}
